import { MainThunkDispatch, State, MainThunkResult } from "../../Reducers"
import { successMessage, failMessage } from "../Client/MessagesActions"
import { userAPIInit } from "../API/AuthenticationActions"
import Amplify from "@aws-amplify/core"
import Auth from "@aws-amplify/auth"
import { AuthUserType } from "../Types"
import { safePromise } from "../../Helpers/Functions/promise"
import { pollAPI, killPoll } from "../Client/NetworkActions"
import { apiEndpoints } from "../../Routes/api"
import { CognitoAuth, CognitoAuthSession } from "amazon-cognito-auth-js"
import {
    XOriginMessageType,
    getTypedMessage
} from "../../Models/Client/XOriginMessage"
import { randID } from "../../Helpers/Functions/random"
import { reportError } from "../../Helpers/Functions/sentry"
import { getUserPlanAttributes } from "../../Helpers/Functions/userplan"
import { AuthUser, UserType } from "../../Models/Client/User"
import { push } from "connected-react-router"
import { routePaths } from "../../Routes"
import { asError } from "../../Helpers/Functions/error"

Auth.configure({ authenticationFlowType: "USER_PASSWORD_AUTH" })

const setScope = (url: URL, identityProvider: string, scope: string[]) => {
    if (identityProvider === "Google") {
        url.searchParams.set("scopes", scope.join(","))
    } else {
        url.searchParams.set("scope", scope.join(" "))
    }
}

const obtainOAuthCode =
    (identityProvider: string) =>
    (
        _: MainThunkDispatch,
        getState: () => State
    ): Promise<string | undefined> => {
        const oauth = getState().client.endpoints.cognito.oauth
        const clientID = getState().client.endpoints.cognito.userPoolWebClientId

        if (!oauth) {
            return Promise.resolve(undefined)
        }

        return new Promise((resolve, reject) => {
            let responded = false
            const url = new URL(`https://${oauth.domain}/oauth2/authorize`)
            const handleMessage = (e: MessageEvent) => {
                const message = getTypedMessage(e)

                if (message.type === XOriginMessageType.oauthCode) {
                    responded = true
                    window.removeEventListener("message", handleMessage)

                    if (message.payload.isError) {
                        reject(new Error(message.payload.message))
                        return
                    }

                    resolve(message.payload.value)
                }
            }

            url.searchParams.set("redirect_uri", oauth.redirectSignIn)
            url.searchParams.set("response_type", "code")
            url.searchParams.set("client_id", clientID)
            url.searchParams.set("identity_provider", identityProvider)
            url.searchParams.set("state", randID())
            setScope(url, identityProvider, oauth.scope)

            window.addEventListener("message", handleMessage)

            const popup = window.open(url.toString(), "_blank")
            const interval = window.setInterval(() => {
                if (!responded && popup !== null && popup.closed) {
                    window.clearInterval(interval)
                    resolve(undefined)
                }
            }, 100)
        })
    }

const resolveCognitoSessionFromOAuthCode =
    (code: string) =>
    (
        _: MainThunkDispatch,
        getState: () => State
    ): Promise<CognitoAuthSession> => {
        const oauth = getState().client.endpoints.cognito.oauth
        const clientID = getState().client.endpoints.cognito.userPoolWebClientId
        const poolID = getState().client.endpoints.cognito.userPoolId

        if (!oauth) {
            return Promise.reject(new Error("OAuth parameters are not defined"))
        }

        return new Promise((resolve, reject) => {
            const authClient = new CognitoAuth({
                ClientId: clientID,
                UserPoolId: poolID,
                AppWebDomain: oauth.domain,
                TokenScopesArray: oauth.scope,
                RedirectUriSignIn: oauth.redirectSignIn,
                RedirectUriSignOut: window.location.href
            })
            const webResponse = `${oauth.redirectSignIn}?code=${code}`

            authClient.userhandler = {
                onFailure: error => reject(new Error(String(error))),
                onSuccess: resolve
            }

            authClient.parseCognitoWebResponse(webResponse)
        })
    }

const userLoginFlow =
    (
        processLogin: MainThunkResult<Promise<boolean>>,
        squashSuccessMessage: boolean
    ): MainThunkResult<Promise<void>> =>
    async (dispatch: MainThunkDispatch): Promise<void> => {
        try {
            dispatch({ type: AuthUserType.startSignIn })
            const isProcessed = await dispatch(processLogin)

            if (!isProcessed) {
                // in case user aborts login process
                dispatch({ type: AuthUserType.noSession })
                return
            }

            await dispatch(userAPIInit(true))

            if (!squashSuccessMessage) {
                dispatch(successMessage(`You have successfully logged in`))
            }

            dispatch({ type: AuthUserType.signedIn })
        } catch (error) {
            const e = asError(error)
            const message = /Check if email already used/.test(e.message)
                ? "Please sign in using your password (you previously created your account using one)"
                : e.message === "Username/client id combination not found."
                ? "Email address not registered."
                : e.message
            if (message === "ConfirmUser") {
                throw e
            }
            dispatch(userLogout(true))
            dispatch(failMessage(`Could not log in. ${message}`, e))

            throw e
        }
    }

export const initAuth =
    () => async (dispatch: MainThunkDispatch, getState: () => State) => {
        try {
            Amplify.configure({ ...getState().client.endpoints.cognito })

            const { error } = await safePromise(dispatch(getValidSession()))

            if (error === undefined) {
                dispatch({ type: AuthUserType.startResumeSession })
                await dispatch(userAPIInit(false))
                dispatch({ type: AuthUserType.resumedSession })
                return
            }

            dispatch({ type: AuthUserType.noSession })
        } catch (e) {
            reportError(e)
            dispatch({ type: AuthUserType.noSession })
        }
    }

export const userLogout =
    (squashMessage: boolean) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const iframeID = "sign-out-iframe"

        try {
            dispatch({ type: AuthUserType.startSignOut })

            const { oauth, userPoolWebClientId } =
                getState().client.endpoints.cognito

            if (!oauth) {
                throw new Error("OAuth parameters are not defined")
            }

            const url = new URL(`https://${oauth.domain}/logout`)
            const iframe = document.createElement("iframe")

            async function handleMessage(e: MessageEvent) {
                const message = getTypedMessage(e)

                if (message.type !== XOriginMessageType.oauthSignedOut) {
                    return
                }

                window.removeEventListener("message", handleMessage)

                const el = document.getElementById(iframeID)

                if (el === null) {
                    return
                }

                document.body.removeChild(el)
            }

            url.searchParams.set("logout_uri", oauth.redirectSignOut)
            url.searchParams.set("client_id", userPoolWebClientId)
            url.searchParams.set("response_type", "code")
            url.searchParams.set("scopes", oauth.scope.join(","))

            iframe.id = iframeID
            iframe.style.width = "0px"
            iframe.style.height = "0px"
            iframe.style.visibility = "hidden"
            iframe.src = url.toString()

            window.addEventListener("message", handleMessage)

            document.body.appendChild(iframe)

            await Auth.signOut()

            dispatch({ type: AuthUserType.signedOut })

            if (!squashMessage) {
                dispatch(successMessage("You have successfully logged out"))
            }
        } catch (error) {
            if (!squashMessage) {
                dispatch(failMessage("Could not log out", error))
            }
            throw error
        }
    }

export const userSignUp =
    (email: string, password: string, squashSuccessMessage: boolean) =>
    async (dispatch: MainThunkDispatch): Promise<boolean> => {
        try {
            dispatch({ type: AuthUserType.startSignUp })

            const result = await Auth.signUp({
                username: email,
                password,
                attributes: { ...getUserPlanAttributes() }
            })

            if (result?.userConfirmed === true) {
                await dispatch(
                    loginAfterSignup(email, password, squashSuccessMessage)
                )
                return true
            } else {
                dispatch({
                    type: AuthUserType.confirmSignup,
                    payload: { email, password }
                })
                return false
            }
        } catch (error) {
            dispatch({ type: AuthUserType.noSession })
            throw error
        }
    }

export const userConfirmSignUp =
    (
        email: string | undefined,
        verificationCode: string,
        squashSuccessMessage: boolean
    ) =>
    async (
        dispatch: MainThunkDispatch,
        getState: () => State
    ): Promise<void> => {
        const user = getState().auth.user
        const confirmEmail =
            user.type === UserType.unconfirmed ? user.email : email

        try {
            if (confirmEmail !== undefined) {
                await Auth.confirmSignUp(confirmEmail, verificationCode)

                if (user.type === UserType.unconfirmed) {
                    try {
                        await dispatch(
                            loginAfterSignup(
                                user.email,
                                user.password,
                                squashSuccessMessage
                            )
                        )
                    } catch (error) {
                        dispatch({ type: AuthUserType.noSession })
                        throw error
                    }
                } else {
                    dispatch(push(routePaths.authSignIn()))
                }
            }
        } catch (error) {
            throw error
        }
    }

const loginAfterSignup =
    (email: string, password: string, squashSuccessMessage: boolean) =>
    async (dispatch: MainThunkDispatch) => {
        const POLL_ID = "LOGIN_SIGN_UP"
        await Auth.signIn(email, password)

        try {
            const endpoint = apiEndpoints.userBase({})

            await Promise.race([
                new Promise(resolve =>
                    dispatch(pollAPI(POLL_ID, endpoint, resolve))
                ),
                // if the polling doesn't finish in 10 seconds, give up and ask to sign in later.
                new Promise((_, reject) => setTimeout(reject, 10000))
            ])
        } catch (e) {
            throw new Error(
                "You have successfully signed up, but there was a temporary issue with our API." +
                    "Try to login with the registered email address and password after a while."
            )
        } finally {
            dispatch(killPoll(POLL_ID))
        }

        if (!squashSuccessMessage) {
            dispatch(successMessage("You have successfully signed up"))
        }
        await safePromise(dispatch(userAPIInit(true, true)))
        dispatch({ type: AuthUserType.signedUp })
    }

export const userResendSignUp =
    (email: string) =>
    async (dispatch: MainThunkDispatch): Promise<void> => {
        try {
            await Auth.resendSignUp(email)
            dispatch(successMessage("Verification code resend successful"))
        } catch (error) {
            dispatch(failMessage("Failed to resend verification code", error))
            throw error
        }
    }
export const userLogin = (
    email: string,
    password: string,
    squashSuccessMessage: boolean
): MainThunkResult<Promise<void>> =>
    userLoginFlow(async (dispatch: MainThunkDispatch) => {
        try {
            await Auth.signIn(email, password)
            return true
        } catch (error) {
            if (
                typeof error === "object" &&
                (error as any).code === "UserNotConfirmedException"
            ) {
                dispatch({
                    type: AuthUserType.confirmSignup,
                    payload: { email, password }
                })
                throw Error("ConfirmUser")
            }
            throw error
        }
    }, squashSuccessMessage)

export const userLoginWithGoogle = (
    squashSuccessMessage: boolean,
    expectedEmail?: string
) =>
    userLoginFlow(async (dispatch: MainThunkDispatch) => {
        const code = await dispatch(obtainOAuthCode("Google"))

        if (code === undefined) {
            return false
        }

        const session = await dispatch(resolveCognitoSessionFromOAuthCode(code))

        if (expectedEmail !== undefined) {
            const idTokenPayload = session.getIdToken().decodePayload()

            if (
                !("email" in idTokenPayload) ||
                (idTokenPayload as any).email !== expectedEmail
            ) {
                throw new Error(
                    `Plesae use the email ${expectedEmail} to sign up.`
                )
            }
        }

        return true
    }, squashSuccessMessage)

export const userLoginWithAzure = (provider: string) =>
    userLoginFlow(async (dispatch: MainThunkDispatch) => {
        const code = await dispatch(obtainOAuthCode(provider))

        if (code === undefined) {
            return false
        }

        await dispatch(resolveCognitoSessionFromOAuthCode(code))

        return true
    }, false)

export const userForgotPassword =
    (username: string) =>
    (dispatch: MainThunkDispatch): Promise<void> => {
        return new Promise((resolve, reject) => {
            Auth.forgotPassword(username)
                .then(_ => {
                    dispatch(
                        successMessage(
                            `Please check your email to reset your password`
                        )
                    )
                    resolve()
                })
                .catch(error => {
                    const message =
                        error && error.message
                            ? error.message
                            : "Could not reset password"
                    dispatch(failMessage(message, error))
                    reject(error)
                })
        })
    }

export const userResetPassword =
    (username: string, code: string, newPassword: string) =>
    (dispatch: MainThunkDispatch): Promise<void> => {
        return new Promise((resolve, reject) => {
            Auth.forgotPasswordSubmit(username, code, newPassword)
                .then(_ => {
                    const message = [
                        "Password successfully reset.",
                        "Please log in with your new credentials."
                    ].join(" ")
                    dispatch(successMessage(message))
                    resolve()
                })
                .catch(error => {
                    const message =
                        error && error.message
                            ? error.message
                            : "Could not reset password"
                    dispatch(failMessage(message, error))
                    reject(error)
                })
        })
    }

export const changePassword =
    (oldPassword: string, newPassword: string) =>
    (dispatch: MainThunkDispatch) => {
        Auth.currentAuthenticatedUser()
            .then(user => Auth.changePassword(user, oldPassword, newPassword))
            .then(_ => dispatch(successMessage(`Password change successful`)))
            .catch(error =>
                dispatch(
                    failMessage(
                        error && error.message
                            ? error.message
                            : "Could not update password",
                        error
                    )
                )
            )
    }

export const getValidSession = () => async (dispatch: MainThunkDispatch) => {
    const { data, error } = await safePromise(Auth.currentSession())

    if (error !== undefined) throw error

    if (data === undefined || !data.isValid()) {
        dispatch({ type: AuthUserType.signedOut })
        throw Error("Invalid user session.")
    } else {
        return data.getAccessToken().getJwtToken()
    }
}

export const validateUserCredentials =
    (email: string, password: string): MainThunkResult<Promise<void>> =>
    async (dispatch: MainThunkDispatch) => {
        dispatch({ type: AuthUserType.startSignIn })
        try {
            const user = await Auth.signIn(email, password)
            if (user?.challengeName === "SOFTWARE_TOKEN_MFA") {
                dispatch({
                    type: AuthUserType.verifyOTP,
                    payload: {
                        currentUser: user
                    }
                })
            } else {
                const code = await Auth.setupTOTP(user)
                dispatch({
                    type: AuthUserType.verifyOTP,
                    payload: {
                        currentUser: user,
                        code
                    }
                })
            }
        } catch (error) {
            if (
                typeof error === "object" &&
                (error as any).code === "UserNotConfirmedException"
            ) {
                dispatch({
                    type: AuthUserType.confirmSignup,
                    payload: { email, password }
                })
                throw Error("ConfirmUser")
            }
            const e = asError(error)
            const message = /Check if email already used/.test(e.message)
                ? "Please sign in using your password (you previously created your account using one)"
                : e.message === "Username/client id combination not found."
                ? "Email address not registered."
                : e.message
            dispatch(userLogout(true))
            dispatch(failMessage(`Could not log in. ${message}`, e))
            throw e
        }
    }

export const verifyOTP = (
    user: AuthUser,
    code: string,
    squashSuccessMessage: boolean
): MainThunkResult<Promise<void>> =>
    userLoginFlow(async () => {
        if (user?.challengeName !== "SOFTWARE_TOKEN_MFA") {
            await Auth.verifyTotpToken(user, code)
            await Auth.setPreferredMFA(user, "TOTP")
        } else {
            await Auth.confirmSignIn(user, code, "SOFTWARE_TOKEN_MFA")
        }
        return true
    }, squashSuccessMessage)

export const skipOTPVerification = (
    squashSuccessMessage: boolean
): MainThunkResult<Promise<void>> =>
    userLoginFlow(async () => true, squashSuccessMessage)
